import React from "react";
import { EmptyState } from "../../../../ForBrands/ui/BrandUI";
import CollapsibleCard from "../../../../ForBrands/ui/CollapsibleCard";
import { ArchetypeDot } from "../../../../ForBrands/ui/LegendItem";
import { usePhoneTheme } from "../../../../ForBrands/useIsPhone";
import { EMPTY } from "../../../../../utils/perfFormat";
import { rotationView } from "../teamPerformanceModel";
import { CardNote } from "./perfPieces";

// Rotation (BUILD_SPEC B7, Jayson's T11): every rotation player and anyone
// else in the top 12 by minutes, in the payload's minutes order, fill-ins
// greyed: archetype (its data colour, ringed), position ("*" when inferred
// from height), height, class, games, minutes, points, rebounds, assists
// and the share of minutes with a mini bar (20% fills it). A registered
// CACHE athlete's name opens their profile (`onOpenProfile(id)`); nobody
// else's does. Desktop: a table that scrolls sideways inside the card;
// phones: one card per player. No rotation on file: the tab's empty state.

const cx = (...parts) => parts.filter(Boolean).join(" ");

const COLUMNS = [
  ["pos", "Pos", "Position"],
  ["ht", "Ht", "Height"],
  ["class", "Class", "Graduating class"],
  ["gp", "GP", "Games played"],
  ["mpg", "MPG", "Minutes per game"],
  ["ppg", "PPG", "Points per game"],
  ["rpg", "RPG", "Rebounds per game"],
  ["apg", "APG", "Assists per game"],
];

const Name = ({ row, onOpenProfile, className = "" }) => {
  if (row.profile && onOpenProfile) {
    return (
      <button
        type="button"
        onClick={() => onOpenProfile(row.profile)}
        aria-label={`Open ${row.name}'s profile`}
        className={cx("text-left underline-offset-2 hover:text-bm-orange hover:underline", className)}
      >
        {row.name}
      </button>
    );
  }
  return <span className={className}>{row.name}</span>;
};

const Archetype = ({ row }) =>
  row.archetype ? (
    <span className="inline-flex min-w-0 items-center gap-[6px]">
      <ArchetypeDot color={row.color} size={9} />
      <span className="truncate">{row.archetype}</span>
    </span>
  ) : (
    <span className="text-bm-tertiary">{EMPTY}</span>
  );

const ShareBar = ({ row }) => (
  <span className="flex items-center gap-2">
    <span className="w-[46px] shrink-0 text-right tabular-nums">{row.share}</span>
    <span aria-hidden="true" className="relative block h-[6px] w-16 shrink-0 overflow-hidden rounded-full bg-bm-mutedbg">
      <span className="absolute inset-y-0 left-0 rounded-full bg-bm-orange" style={{ width: `${row.shareFill}%` }} />
    </span>
  </span>
);

// Alignment is added per cell (two of a kind would clash).
const TH = "bm-th !h-10 !px-3";
const TD = "bm-td !px-3 !py-[10px] bm-body-m";

const RotationTable = ({ rows, onOpenProfile }) => (
  <div className="overflow-x-auto rounded-[8px] border border-bm-border">
    <table className="w-full min-w-[860px] border-separate border-spacing-0 tabular-nums">
      <thead>
        <tr>
          <th scope="col" className={cx(TH, "!pl-4 text-left")}>
            Player
          </th>
          <th scope="col" className={cx(TH, "text-left")}>
            Archetype
          </th>
          {COLUMNS.map(([key, label, title]) => (
            <th key={key} scope="col" className={cx(TH, "text-center")} title={title}>
              {/* The short head for the eye (and the mouse's title), its meaning for a screen reader. */}
              <span aria-hidden="true">{label}</span>
              <span className="sr-only">{title}</span>
            </th>
          ))}
          <th scope="col" className={cx(TH, "text-left")}>
            Share of minutes
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const tone = r.rotation ? "text-bm-ink" : "text-bm-muted";
          return (
            <tr key={r.key} className={r.rotation ? "hover:bg-bm-subtle" : "bg-bm-subtle"}>
              <th scope="row" className={cx("bm-td !py-[10px] !pl-4 text-left font-normal", tone)}>
                <Name row={r} onOpenProfile={onOpenProfile} className="bm-label-l" />
                {/* The grey row says "fill-in" to the eye only; say it in words too (the phone card prints it). */}
                {!r.rotation && <span className="sr-only">, fill-in outside the rotation</span>}
              </th>
              <td className={cx(TD, "text-left", tone)}>
                <Archetype row={r} />
              </td>
              {[r.position, r.height, r.gradClass, r.gp, r.mpg, r.ppg, r.rpg, r.apg].map((value, i) => (
                <td key={COLUMNS[i][0]} className={cx(TD, "text-center", tone)}>
                  {value}
                </td>
              ))}
              <td className={cx(TD, "text-left", tone)}>
                <ShareBar row={r} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const RotationList = ({ rows, onOpenProfile }) => (
  // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
  <ul role="list" aria-label="Rotation" className="flex flex-col">
    {rows.map((r) => (
      <li key={r.key} className={cx("flex flex-col gap-[6px] border-b border-bm-mutedbg py-3 last:border-b-0", !r.rotation && "opacity-70")}>
        <span className="flex min-w-0 items-baseline justify-between gap-2">
          <Name row={r} onOpenProfile={onOpenProfile} className="min-w-0 truncate bm-label-l text-bm-ink" />
          <span className="shrink-0 bm-body-xs text-bm-muted">{[r.position, r.height, r.gradClass].join(" · ")}</span>
        </span>
        <span className="flex min-w-0 items-center justify-between gap-2 bm-body-s text-bm-ink">
          <Archetype row={r} />
          {!r.rotation && <span className="shrink-0 bm-body-xs text-bm-muted">Fill-in</span>}
        </span>
        <span className="bm-body-xs tabular-nums text-bm-muted">
          {`${r.gp} GP · ${r.mpg} MPG · ${r.ppg} PPG · ${r.rpg} RPG · ${r.apg} APG`}
        </span>
        <span className="flex items-center justify-between gap-2 bm-body-xs text-bm-muted">
          <span>Share of minutes</span>
          <ShareBar row={r} />
        </span>
      </li>
    ))}
  </ul>
);

const RotationCard = ({ makeup, onOpenProfile, collapse }) => {
  const phone = usePhoneTheme();
  const { rows, note, listNote } = rotationView(makeup);
  return (
    <CollapsibleCard title="Rotation" titleAs="h3" {...collapse}>
      {rows.length ? (
        <>
          {phone ? <RotationList rows={rows} onOpenProfile={onOpenProfile} /> : <RotationTable rows={rows} onOpenProfile={onOpenProfile} />}
          {/* The phone cards have no grey rows: each fill-in says so itself. */}
          <CardNote>{phone ? listNote : note}</CardNote>
        </>
      ) : (
        <EmptyState>No rotation on file for this squad.</EmptyState>
      )}
    </CollapsibleCard>
  );
};

export default RotationCard;
